import IEmpresas from "../interfaces/IEmpresas";
import IFiltroTurmaRegular from "../interfaces/IFiltroTurmaRegular";
import IRelatorio_Colaboradores from "../interfaces/IRelatorio_Colaboradores";
import IReports from "../interfaces/IReports";
import ITurmaRegularOfertaDisciplina from "../interfaces/ITurmaRegularOfertaDisciplina";
import { IPeriodo, IUsuario } from "../interfaces/IUsuario";
import {
  getEstadoCivil,
  getGrauInstrucao,
  getTipoDisciplina,
} from "../utilities/constantes";

const defaultProps: IReports = {
  // Configurações gerais do relatório
  config: {
    // Orientação da página: "portrait" (retrato) ou "landscape" (paisagem)
    orientation: "landscape",
    reportName: "Relatório de Turmas Regulares",
    // Modo de exibição das informações de página
    // "footer-simple": Exibe no rodapé "Página X de Y, Emitido por Operador - Em: Data - Hora"
    // "header-detailed": Exibe no cabeçalho "Pág.: X / Y, data: DD/MM/AAAA, hora: HH:MM, emitido por Operador"
    pageInfoMode: "header-detailed",

    // Altura do cabeçalho em milímetros
    headerHeight: 35,

    // Altura do rodapé em milímetros
    footerHeight: 5,

    // Tamanho da fonte base em pontos
    fontSize: 10,

    // Tamanho da fonte do título em pontos
    titleFontSize: 14,

    // Tamanho da fonte dos detalhes em pontos
    detailFontSize: 10,

    // Família da fonte: "arial", "times", "courier", "calibri", "tahoma"
    fontFamily: "arial",

    // Alternar cores das linhas nas tabelas
    alternateRowColors: true,
    // Nota: O sistema usa fatores de utilização de espaço diferentes por orientação:
    // - 130% para orientação vertical (portrait)
    // - 110% para orientação horizontal (landscape)
    // Isso permite otimizar o uso do espaço disponível em cada página

    // Espaçamento interno das células em pontos
    cellPadding: 4,

    // Configuração do logo
    logo: {
      // URL do logo
      url: "https://exemplo.com/logo.png",

      // Tamanho do logo em milímetros
      size: 40,

      // Formato do logo: "square" (quadrado) ou "horizontal"
      format: "square",

      // Margem entre o logo e o texto do cabeçalho em milímetros
      textMargin: 5,
    },

    marginRight: 10,
    marginLeft: 10,
    marginTop: 5,
    marginBottom: 5,
  },

  // Linhas do cabeçalho
  headerLines: [],

  // Linhas do rodapé
  footerLines: [
    { text: "Documento Confidencial", align: "left" },
    { text: "Uso Interno", align: "left" },
  ],

  // Nome do operador que emitiu o relatório
  operatorName: "",

  // Conteúdo do relatório - Array com todos os elementos que compõem o relatório
  content: [],
};

// Função para renderizar uma linha em formato de tabela HTML
const renderDispilinasLine = (
  Disciplinas: ITurmaRegularOfertaDisciplina[]
): string => {
  if (Disciplinas === undefined || Disciplinas.length === 0)
    return `<tr><td colspan="3" style="text-align: center; padding: 4px;">Nenhuma disciplina cadastrada</td></tr>`;

  return `
  <div style="padding: 8px;">
  <table style="width: 60%;" border='0' cellspacing='0' cellpadding='0'>
  ${Disciplinas?.map(
    (disciplina) => `
    <tr>
      <td style="border: none; padding: 2px;">
        ${disciplina.Codigo || ""} - ${disciplina.Descricao || ""}
      </td>
      <td style="border: none; padding: 2px;">
        CHR: ${disciplina.Chr || 0}
      </td>
      <td style="border: none; padding: 2px;">
        Tipo: ${getTipoDisciplina(disciplina.Tipo || "")}
      </td>
    </tr>
  `
  ).join("")}
  </table>
  </div>
  `;
};

const buildTurmasRegulares = (
  data: IRelatorio_Colaboradores[],
  formData: IFiltroTurmaRegular,
  empresaSelecionada?: IEmpresas,
  periodoSelecionado?: IPeriodo,
  usuario?: IUsuario
): IReports => {
  const report: IReports = {
    ...defaultProps,
    config: {
      ...defaultProps.config,
      logo: {
        url: empresaSelecionada?.Logo,
      },
    },
    headerLines: [
      { text: empresaSelecionada?.IdRelatorio, align: "left" },
      {
        text: `Relação de Turmas Regulares - ${periodoSelecionado?.Descricao}`,
        align: "left",
      },
      {
        text:
          formData.classificadoPor == "C"
            ? "Organizado por Código"
            : "Organizado por Descrição",
        align: "left",
      },
    ],
    operatorName: usuario?.Nome,

    content: [
      {
        compact: true,
        type: "table",
        id: `tabela-turmas-regulares`,
        columns: [
          {
            name: "CÓDIGO",
            headerAlignment: "left",
            dataAlignment: "left",
            width: "6%",
          },
          {
            name: "DESCRIÇÃO",
            headerAlignment: "left",
            dataAlignment: "left",
          },
          {
            name: "CURSO",
            headerAlignment: "left",
            dataAlignment: "left",
          },
          {
            name: "SALA",
            headerAlignment: "left",
            dataAlignment: "left",
            width: "10%",
          },
          {
            name: "TURNO",
            headerAlignment: "center",
            dataAlignment: "center",
            width: "5%",
          },
          {
            name: "G / S",
            headerAlignment: "center",
            dataAlignment: "center",
            width: "5%",
          },
          {
            name: "V.OCUP",
            headerAlignment: "center",
            dataAlignment: "center",
            width: "5%",
          },
          {
            name: "V.PREV",
            headerAlignment: "center",
            dataAlignment: "center",
            width: "5%",
          },
          {
            name: "C.H.TOT",
            headerAlignment: "center",
            dataAlignment: "center",
            width: "8%",
          },
        ],
        data: data.flatMap((item: any) => {
          const mainRow = [
            item.CodigoTurma || "",
            `${item.DescricaoTurma}`,
            `${item.DescricaoCurso}`,
            item.Sala || "",
            item.Turno || "",
            `${item.Nivel} / ${item.Sequencia}`,
            item.VagasOcupadas || 0,
            item.VagasPrevistas || 0,
            item.CargaHorariaTotal || 0,
          ];
          if (formData.exibirGrade === "S") {
            const detailRow = [
              {
                colspan: 9,
                text: renderDispilinasLine(item.Disciplinas),
              },
            ];
            return [mainRow, detailRow];
          } else {
            return [mainRow];
          }
        }),
      },
    ],
  };

  return report;
};

export default buildTurmasRegulares;
